<template>
  <ButtonTeleporter v-if="puedeCorregirIdentificacion">
    <ButtonDefault
      color="yellow"
      title="Corregir tipo y número de identificación"
      @click="modalCorregirIdentificacion?.open()"
    >
      <MdiIcon icono="mdiCardAccountDetailsOutline" class="shrink-0 size-4" />
      Corregir identificación
    </ButtonDefault>
  </ButtonTeleporter>
  <ButtonTeleporter v-if="puedeConfigurarCampos">
    <ButtonDefault
      color="gray"
      title="Parametrizar los campos del formulario de pacientes"
      @click="abrirConfiguracionCampos"
    >
      <MdiIcon icono="mdiTuneVariant" class="shrink-0 size-4" />
      Configurar formulario
    </ButtonDefault>
  </ButtonTeleporter>
  <ModalCorregirIdentificacion
    v-if="puedeCorregirIdentificacion"
    ref="modalCorregirIdentificacion"
    :cope-id="form.copeId as number"
    :identificacion-actual="form.copeIdentificacion"
    :tipo-identificacion-actual="form.copeTipoIdentificacion"
    :tipos-identificacion="tipos.tipoidentificacion"
    @actualizado="onIdentificacionCorregida"
  />
  <PacientesConfiguracionModal
    v-if="puedeConfigurarCampos"
    ref="modalConfiguracionCampos"
    :tipos="tipos"
    @guardado="onConfiguracionCamposGuardada"
  />
  <SimpleSkeleton
    v-if="cargando"
    class="p-5"
    :options="getSkeleton(4, 6, { altoF: '150px' })"
  />
  <div v-else class="bg-white rounded-xl overflow-hidden dark:bg-slate-900">
    <div class="px-6 py-4 border-b border-gray-400 dark:border-gray-700">
      <div class="grid grid-cols-4 gap-4 lg:gap-6">
        <template v-if="tipos">
          <div class="mb-4 sm:mb-6 relative">
            <SelectDefault
              id="tipo_identificacion"
              v-model="form.copeTipoIdentificacion"
              label="Tipo de identificación"
              :required="true"
              :readonly="form.copeId ? true : false"
              :options="tipos.tipoidentificacion"
              :map-options="
                (item: IConfMaestra) => {
                  return {
                    optionText: item.comaNombrelargo,
                    optionValue: item.comaId
                  }
                }
              "
              :error="showError('form.copeTipoIdentificacion')"
              @change="validatePatientIdentification"
            />
          </div>
        </template>
        <div class="mb-4 sm:mb-8 col-span-3 relative">
          <div class="flex flex-col items-center">
            <div class="flex rounded-lg w-full">
              <div class="block w-full">
                <InputDefault
                  v-model="form.copeIdentificacion"
                  label="Número de identificación"
                  type="text"
                  :required="true"
                  :readonly="form.copeId ? true : false"
                  :error="showError('form.copeIdentificacion')"
                  :class-add="'rounded-e-none'"
                  @keydown.enter="validatePatient"
                />
              </div>
              <div class="flex items-end">
                <ButtonDefault
                  v-if="!form.copeId && !route.params?.id"
                  size="md"
                  class-add="rounded-s-none"
                  :disabled="form.copeId ? true : false"
                  title="Validar documento"
                  @click="validatePatient"
                >
                  <div
                    v-if="isSearching"
                    class="animate-spin inline-block size-4 border-[3px] border-current border-t-transparent text-blue-600 rounded-full dark:text-blue-500"
                    role="status"
                    aria-label="loading"
                  >
                    <span class="sr-only">Loading...</span>
                  </div>
                  <MagnifyingGlassIcon v-else class="shrink-0 size-3.5" />
                </ButtonDefault>
              </div>
            </div>
          </div>
        </div>
        <div class="col-span-4">
          <alert-default
            v-if="!alreadyValidated"
            titulo="Validación obligatoria"
            texto="Debe validar el documento para poder continuar con el formulario."
            color="yellow"
            icono="ExclamationTriangleIcon"
          />
          <alert-default
            v-if="showValidTipid"
            titulo="Información"
            texto="El tipo de identificación no es compatible con la edad de la persona."
            color="blue"
            icono="ExclamationTriangleIcon"
          />
        </div>
        <template v-if="alreadyValidated">
          <div
            v-if="!isFieldHidden('copnNombre1')"
            class="mb-4 sm:mb-8 relative"
          >
            <InputDefault
              v-model="form.copnNombre1"
              label="Primer nombre"
              :upper-case="true"
              type="text"
              :required="isFieldRequired('copnNombre1')"
              :error="showError('form.copnNombre1')"
            />
          </div>
          <div
            v-if="!isFieldHidden('copnNombre2')"
            class="mb-4 sm:mb-8 relative"
          >
            <InputDefault
              v-model="form.copnNombre2"
              label="Segundo nombre"
              :upper-case="true"
              type="text"
              :required="isFieldRequired('copnNombre2')"
              :error="showError('form.copnNombre2')"
            />
          </div>
          <div
            v-if="!isFieldHidden('copnApellido1')"
            class="mb-4 sm:mb-8 relative"
          >
            <InputDefault
              v-model="form.copnApellido1"
              label="Primer apellido"
              :upper-case="true"
              type="text"
              :error="showError('form.copnApellido1')"
              :required="isFieldRequired('copnApellido1')"
            />
          </div>
          <div
            v-if="!isFieldHidden('copnApellido2')"
            class="mb-4 sm:mb-8 relative"
          >
            <InputDefault
              v-model="form.copnApellido2"
              :upper-case="true"
              label="Segundo apellido"
              type="text"
              :required="isFieldRequired('copnApellido2')"
              :error="showError('form.copnApellido2')"
            />
          </div>

          <div
            v-if="!isFieldHidden('coppFechanacimiento')"
            class="mb-4 sm:mb-8 relative"
          >
            <DatePickerDefault
              id="fec_nac"
              v-model="form.coppFechanacimiento"
              :time="false"
              :label="'Fecha nacimiento'"
              :error="showError('form.coppFechanacimiento')"
              :block-future-dates="true"
              :required="isFieldRequired('coppFechanacimiento')"
              @change="validatePatientIdentification"
            />
          </div>
          <div
            v-if="!isFieldHidden('copeTelefono')"
            class="mb-4 sm:mb-8 relative"
          >
            <InputDefault
              v-model="form.copeTelefono"
              label="Número de celular"
              :maxlength="10"
              :show-character-count="true"
              :error="showError('form.copeTelefono')"
              :required="isFieldRequired('copeTelefono')"
            />
          </div>
          <div
            v-if="!isFieldHidden('copeTelalternativo')"
            class="mb-4 sm:mb-8 relative"
          >
            <InputDefault
              v-model="form.copeTelalternativo"
              label="Número de celular alternativo"
              type="number"
              :required="isFieldRequired('copeTelalternativo')"
              :error="showError('form.copeTelalternativo')"
            />
          </div>
          <div v-if="!isFieldHidden('copeEmail')" class="mb-4 sm:mb-8 relative">
            <InputDefault
              v-model="form.copeEmail"
              label="Email"
              :upper-case="true"
              type="text"
              :required="isFieldRequired('copeEmail')"
              :error="showError('form.copeEmail')"
            />
          </div>
          <div
            v-if="!isFieldHidden('copeDireccion')"
            class="mb-4 sm:mb-8 relative"
          >
            <InputDefault
              v-model="form.copeDireccion"
              label="Dirección"
              :upper-case="true"
              type="text"
              :error="showError('form.copeDireccion')"
              :required="isFieldRequired('copeDireccion')"
            />
          </div>
          <div
            v-if="!isFieldHidden('coppTipoBarrio')"
            class="mb-4 sm:mb-8 relative"
          >
            <InputDefault
              v-model="form.coppTipoBarrio"
              label="Barrio"
              :required="isFieldRequired('coppTipoBarrio')"
              :upper-case="true"
              type="text"
              :error="showError('form.coppTipoBarrio')"
            />
          </div>
          <div
            v-if="!isFieldHidden('copeTipoDivipola')"
            class="mb-4 sm:mb-6 relative"
          >
            <SelectSearch
              id="divipola_paciente"
              v-model:search="searchDivipola"
              v-model:value="form.copeTipoDivipola"
              label="Lugar de residencia"
              :clear="true"
              :error="showError('form.copeTipoDivipola')"
              :required="isFieldRequired('copeTipoDivipola')"
              :tabs="true"
              :options="{
                fieldFilter: {
                  mapOptions: (item: IConfDivipola) => {
                    return `${item.nomPais + '-' + item.nomDepartamento + '-' + item.nomMunicipio}`
                  }
                },
                urlSearch: 'divipolaService',
                action: 'searchByName',
                mapOptions: (item: IConfDivipola) => {
                  const departamento = item.nomDepartamento
                    ? `-${item.nomDepartamento}`
                    : ''
                  const municipio = item.nomMunicipio
                    ? `-${item.nomMunicipio}`
                    : ''
                  return {
                    optionText: `${item.nomPais}${departamento}${municipio}`,
                    optionSubText: '',
                    optionValue: item.codiId
                  }
                }
              }"
              :group-field="{
                mapOptions: (item: IConfDivipola) => `${item.nomDepartamento}`
              }"
              :is-group="true"
            />
          </div>
          <template v-if="tipos">
            <div
              v-if="!isFieldHidden('coppTipoDivipola')"
              class="mb-4 sm:mb-6 relative"
            >
              <SelectSearch
                id="divipola_nac_paciente"
                v-model:search="searchDivipolaNacimiento"
                v-model:value="form.coppTipoDivipola"
                :error="showError('form.coppTipoDivipola')"
                label="Lugar de nacimiento"
                :clear="true"
                :required="isFieldRequired('coppTipoDivipola')"
                :tabs="true"
                :options="{
                  fieldFilter: {
                    mapOptions: (item: IConfDivipola) => {
                      return `${item.nomPais + '-' + item.nomDepartamento + '-' + item.nomMunicipio}`
                    }
                  },
                  urlSearch: 'divipolaService',
                  action: 'searchByName',
                  mapOptions: (item: IConfDivipola) => {
                    const departamento = item.nomDepartamento
                      ? `-${item.nomDepartamento}`
                      : ''
                    const municipio = item.nomMunicipio
                      ? `-${item.nomMunicipio}`
                      : ''
                    return {
                      optionText: `${item.nomPais}${departamento}${municipio}`,
                      optionSubText: '',
                      optionValue: item.codiId
                    }
                  }
                }"
                :group-field="{
                  mapOptions: (item: IConfDivipola) => `${item.nomDepartamento}`
                }"
                :is-group="true"
              >
              </SelectSearch>
            </div>
            <div
              v-if="!isFieldHidden('coppTipoZona')"
              class="mb-4 sm:mb-6 relative"
            >
              <SelectDefault
                id="zone"
                v-model="form.coppTipoZona"
                label="Zona"
                :options="tipos.tipozona"
                :error="showError('form.coppTipoZona')"
                :required="isFieldRequired('coppTipoZona')"
                :map-options="
                  (item: IConfMaestra) => {
                    return {
                      optionText: item.comaNombrelargo,
                      optionValue: item.comaId
                    }
                  }
                "
              >
              </SelectDefault>
            </div>
            <div
              v-if="!isFieldHidden('coppTipoNiveleducativo')"
              class="mb-4 sm:mb-6 relative"
            >
              <SelectDefault
                id="educacion"
                v-model="form.coppTipoNiveleducativo"
                label="Educación"
                :options="tipos.tiponvleducacion"
                :required="isFieldRequired('coppTipoNiveleducativo')"
                :error="showError('form.coppTipoNiveleducativo')"
                :map-options="
                  (item: IConfMaestra) => {
                    return {
                      optionText: item.comaNombrelargo,
                      optionValue: item.comaId
                    }
                  }
                "
              >
              </SelectDefault>
            </div>
            <div
              v-if="!isFieldHidden('coppTipoOcupacion')"
              class="mb-4 sm:mb-6 relative"
            >
              <SelectSearch
                :id="'ocupacion'"
                v-model:value="form.coppTipoOcupacion"
                v-model:search="searchOcupacion"
                label="Ocupación"
                placeholder="Buscar ocupación"
                :list="tipos.tipoocupacion"
                :initial-data="true"
                :required="isFieldRequired('coppTipoOcupacion')"
                :error="showError('form.coppTipoOcupacion')"
                :options="{
                  name: 'tipo_ocupacion_persona',
                  fieldFilter: {
                    mapOptions: (item: IConfMaestra) => {
                      return `${item.comaNombrelargo}`
                    }
                  },
                  mapOptions: (item: IConfMaestra) => {
                    return {
                      optionText: item.comaNombrelargo,
                      optionValue: item.comaId
                    }
                  }
                }"
              />
            </div>
            <div
              v-if="!isFieldHidden('coppTipoSexo')"
              class="mb-4 sm:mb-6 relative"
            >
              <SelectDefault
                id="sexo"
                v-model="form.coppTipoSexo"
                label="Sexo"
                placeholder=" "
                :options="tipos.tiposexo"
                :map-options="
                  (item: IConfMaestra) => {
                    return {
                      optionText: item.comaNombrelargo,
                      optionValue: item.comaId
                    }
                  }
                "
                :error="showError('form.coppTipoSexo')"
                :required="isFieldRequired('coppTipoSexo')"
              />
            </div>
            <div
              v-if="!isFieldHidden('coppTipoGenero')"
              class="mb-4 sm:mb-6 relative"
            >
              <SelectDefault
                id="genero"
                v-model="form.coppTipoGenero"
                label="Género"
                :options="tipos.tipogenero"
                :map-options="
                  (item: IConfMaestra) => {
                    return {
                      optionText: item.comaNombrelargo,
                      optionValue: item.comaId
                    }
                  }
                "
                :error="showError('form.coppTipoGenero')"
                :required="isFieldRequired('coppTipoGenero')"
              />
            </div>
            <div
              v-if="!isFieldHidden('coppTipoOrientacion')"
              class="mb-4 sm:mb-6 relative"
            >
              <SelectSearch
                id="orientacion"
                v-model:value="form.coppTipoOrientacion"
                v-model:search="searchOrientacion"
                label="Orientación sexual"
                :clear="true"
                :initial-data="true"
                :required="isFieldRequired('coppTipoOrientacion')"
                :error="showError('form.coppTipoOrientacion')"
                :list="tipos.tipoorientacion"
                :options="{
                  name: 'tipo_orientacion_persona',
                  fieldFilter: {
                    mapOptions: (item: IConfMaestra) => {
                      return `${item.comaNombrelargo}`
                    }
                  },
                  mapOptions: (item: IConfMaestra) => {
                    return {
                      optionText: item.comaNombrelargo,
                      optionValue: item.comaId
                    }
                  }
                }"
              />
            </div>
            <div
              v-if="!isFieldHidden('coppTipoEstadocivil')"
              class="mb-4 sm:mb-6 relative"
            >
              <SelectDefault
                id="civil"
                v-model="form.coppTipoEstadocivil"
                label="Estado civil"
                :options="tipos.tipoestadocivil"
                :required="isFieldRequired('coppTipoEstadocivil')"
                :error="showError('form.coppTipoEstadocivil')"
                :map-options="
                  (item: IConfMaestra) => {
                    return {
                      optionText: item.comaNombrelargo,
                      optionValue: item.comaId
                    }
                  }
                "
              />
            </div>

            <div
              v-if="!isFieldHidden('coppTipoPoblacion')"
              class="mb-4 sm:mb-6 col-span-2 relative"
            >
              <SelectSearch
                id="poblacion"
                v-model:value="form.coppTipoPoblacion"
                v-model:search="searchPoblacion"
                label="Población (Principal)"
                :initial-data="true"
                :clear="true"
                :list="tipos.tipopoblacion"
                :required="isFieldRequired('coppTipoPoblacion')"
                :error="showError('form.coppTipoPoblacion')"
                :options="{
                  name: 'tipo_ocupacion_persona',
                  fieldFilter: {
                    mapOptions: (item: IConfMaestra) => {
                      return `${item.comaNombrelargo}`
                    }
                  },
                  mapOptions: (item: IConfMaestra) => {
                    return {
                      optionText: item.comaNombrelargo,
                      optionValue: item.comaId
                    }
                  }
                }"
                @change="onChangePoblacion"
              />
            </div>
            <div
              v-if="!isFieldHidden('coppTipoListPoblaciones')"
              class="mb-4 sm:mb-6 relative col-span-2"
            >
              <SelectAvanced
                id="multiples_poblaciones"
                v-model="poblacion_adicionales"
                label="Población (Adicionales) "
                multiple
                :options="tipoProblacionesMultipples"
                :searchable="true"
                :required="isFieldRequired('coppTipoListPoblaciones')"
                :error="showError('form.coppTipoListPoblaciones')"
                :map-options="
                  (item: object) => {
                    const i = item as IConfMaestra
                    return {
                      optionText: i.comaNombrelargo,
                      optionValue: i.comaNombrelargo
                    }
                  }
                "
              />
            </div>
            <div
              v-if="!isFieldHidden('coppTipoEtnia')"
              class="mb-4 sm:mb-6 relative"
            >
              <SelectSearch
                id="etnia-select"
                v-model:value="form.coppTipoEtnia"
                v-model:search="searchEtnia"
                label="Etnia"
                :clear="true"
                :initial-data="true"
                :required="isFieldRequired('coppTipoEtnia')"
                :error="showError('form.coppTipoEtnia')"
                :list="tipos.tipoetnia"
                :options="{
                  name: 'tipo_etnia_persona',
                  fieldFilter: {
                    mapOptions: (item: IConfMaestra) => {
                      return `${item.comaNombrelargo}`
                    }
                  },
                  mapOptions: (item: IConfMaestra) => {
                    return {
                      optionText: item.comaNombrelargo,
                      optionValue: item.comaId
                    }
                  }
                }"
              />
            </div>
            <div
              v-if="!isFieldHidden('coppTipoIncapacidad')"
              class="mb-4 sm:mb-6 relative"
            >
              <SelectSearch
                id="discapacidad"
                v-model:value="form.coppTipoIncapacidad"
                v-model:search="searchDiscapacidad"
                label="Discapacidad"
                :initial-data="true"
                :clear="true"
                :required="isFieldRequired('coppTipoIncapacidad')"
                :error="showError('form.coppTipoIncapacidad')"
                :list="tipos.tipoincapacidad"
                :options="{
                  name: 'tipo_incapacidad_persona',
                  fieldFilter: {
                    mapOptions: (item: IConfMaestra) => {
                      return `${item.comaNombrelargo}`
                    }
                  },
                  mapOptions: (item: IConfMaestra) => {
                    return {
                      optionText: item.comaNombrelargo,
                      optionValue: item.comaId
                    }
                  }
                }"
              />
            </div>
            <div
              v-if="form.copeId && !isFieldHidden('coppTipoEstado')"
              class="mb-4 sm:mb-6 relative"
            >
              <SelectDefault
                id="estado"
                v-model="form.coppTipoEstado"
                label="Estado"
                :options="tipos.tipoestado"
                :required="isFieldRequired('coppTipoEstado')"
                :error="showError('form.coppTipoEstado')"
                :map-options="
                  (item: IConfMaestra) => {
                    return {
                      optionText: item.comaNombrelargo,
                      optionValue: item.comaNombrecorto
                    }
                  }
                "
              />
            </div>
          </template>
        </template>
      </div>
    </div>
    <div class="pr-2 pt-6 flex justify-end gap-2">
      <div class="grow flex justify-end gap-2">
        <slot name="buttons-pacietes-extra"> </slot>
      </div>
      <div class="flex justify-end gap-2">
        <ButtonDefault v-if="props.isModal" color="red" @click="cerrarModal">
          Cerrar
        </ButtonDefault>
        <ButtonDefault
          v-if="alreadyValidated && form.copeIdentificacion"
          color="blue"
          @click="createRecord"
        >
          Guardar
        </ButtonDefault>
      </div>
    </div>
  </div>
</template>
<script setup lang="ts">
import {
  closeModal,
  getErrores,
  getSkeleton,
  validateTypeIdentification,
  generarUuid
} from '@/modules/Core/usings/generales.js'
import { validatePermiso, validateRole } from '@/modules/Core/usings/generales'
import { ref, computed, onMounted, inject, type Ref } from 'vue'
import { useRouter, useRoute } from 'vue-router'
import type { ICookies } from '@/modules/Core/interfaces'
import type {
  IConfPersonasEmpleado,
  IConfPersonasPaciente
} from '@/modules/Administracion/interfaces'
import type {
  CampoPacienteConfiguracionKey,
  IPacientesConfiguracionCampos
} from '@/modules/Administracion/interfaces/IPacientesConfiguracion'
import type { IConfConfiguracion } from '@/modules/Configuracion/interfaces'
import type { ITiposGeneralesContrato } from '@/modules/Contratacion/interfaces'
import type {
  IConfDivipola,
  IConfEntidades,
  IConfMaestra,
  IConfRelacionesEntidades
} from '@/modules/Configuracion/interfaces'

import { MagnifyingGlassIcon } from '@heroicons/vue/24/outline'
import type { AxiosError } from 'axios'
import {
  configuracionServicios,
  contratoService,
  entidadService,
  maestraService,
  personaService,
  divipolaService
} from '@/services'
import type {
  IAuthAssignment,
  IAuthUser,
  IAuthUserRole,
  IUserLoged
} from '@/modules/Autenticacion/interfaces'
import type { ITipoGeneralesPersona } from '@/modules/Administracion/interfaces/IPacienteData'
import SelectAvanced from '@/modules/Core/components/Form/SelectAvanced.vue'
import ModalCorregirIdentificacion from '@/modules/Administracion/components/ModalCorregirIdentificacion.vue'
import PacientesConfiguracionModal from '@/modules/Administracion/components/PacientesConfiguracionModal.vue'
import { coincideIdentificacion, normalizarIdentificacion } from '@/modules/Administracion/utils/identificacionPersona'
import type { ICorreccionIdentificacion } from '@/modules/Administracion/interfaces'
import {
  useValidator,
  validators
} from '@/modules/Core/composables/useValidator'
import {
  CATEGORIA_CONFIGURACION_PACIENTES,
  CODIGO_CONFIGURACION_PACIENTES,
  camposPacienteConfigurables,
  crearConfiguracionCamposPaciente,
  normalizarConfiguracionCamposPaciente
} from '@/modules/Administracion/utils/pacientesConfiguracion'

defineSlots<{
  'buttons-pacietes-extra': () => unknown
}>()

const props = defineProps<{
  isModal?: boolean
  copeId?: number | null | undefined
  modalId?: string
}>()
const poblacion_adicionales = ref<string[]>([])
// const poblacion_principal = ref<number>()
const etnia = ref<string | null>(null)
const route = useRoute()
const router = useRouter()
const errores = ref([])
const entidades = ref<IConfEntidades[]>([])

const tipos: Ref<ITipoGeneralesPersona> = ref({
  tipoidentificacion: [],
  tipopais: [],
  tipoestado: [],
  tipozona: [],
  tipopoblacion: [],
  tiponvleducacion: [],
  tipoocupacion: [],
  tiposexo: [],
  tipoestadocivil: [],
  tipoincapacidad: [],
  tipoetnia: [],
  tiporegimen: [],
  tipoorientacion: [],
  tipogenero: []
})
const $cookies = inject<ICookies>('$cookies')
const isSearching = ref(false)
const alreadyValidated = ref(false)
const showValidTipid = ref(false)
const searchDivipola = ref<string | null>(null)
const searchDivipolaNacimiento = ref<string | null>(null)
const searchOcupacion = ref<string | null>(null)
const searchOrientacion = ref<string | null>(null)
const searchDiscapacidad = ref<string | null>(null)
const searchPoblacion = ref<string | null>(null)
const searchEtnia = ref<string | null>(null)
const cargando = ref(false)
const emit = defineEmits(['nuevoPaciente'])
const form = ref<IConfPersonasPaciente>({} as IConfPersonasPaciente)
const altaCruzada = ref(false)
const tiposContratos = ref<ITiposGeneralesContrato>()
const _userSession = $cookies?.get('user') as IUserLoged
const tipoProblacionesMultipples = ref<IConfMaestra[]>([])
const camposConfiguracionPaciente = ref<IPacientesConfiguracionCampos>(
  crearConfiguracionCamposPaciente()
)
const registroConfiguracionPacientes = ref<IConfConfiguracion | null>(null)
const modalConfiguracionCampos = ref<InstanceType<
  typeof PacientesConfiguracionModal
> | null>(null)
const modalCorregirIdentificacion = ref<InstanceType<
  typeof ModalCorregirIdentificacion
> | null>(null)

const puedeCorregirIdentificacion = computed(
  () => !props.isModal && !altaCruzada.value && !!form.value.copeId && validatePermiso(['editarPaciente'])
)
const puedeConfigurarCampos = computed(() => !props.isModal && validateRole([]))

const isFieldRequired = (campo: CampoPacienteConfiguracionKey) =>
  camposConfiguracionPaciente.value[campo].requerido &&
  !camposConfiguracionPaciente.value[campo].oculto

const isFieldHidden = (campo: CampoPacienteConfiguracionKey) =>
  camposConfiguracionPaciente.value[campo].oculto

const cargarConfiguracionCamposPaciente = async (
  params: { conservarActual?: boolean; actualizarCampos?: boolean } = {}
) => {
  const { conservarActual = false, actualizarCampos = true } = params

  try {
    const {
      data: { body }
    } = await configuracionServicios.list({
      cocoCategoria: CATEGORIA_CONFIGURACION_PACIENTES,
      cocoCodigo: CODIGO_CONFIGURACION_PACIENTES
    })
    const registro =
      body?.find(item => item.cocoCodigo === CODIGO_CONFIGURACION_PACIENTES) ??
      null
    registroConfiguracionPacientes.value = registro
    if (actualizarCampos) {
      camposConfiguracionPaciente.value = normalizarConfiguracionCamposPaciente(
        registro?.cocoValue
      )
    }
  } catch {
    if (!conservarActual) {
      camposConfiguracionPaciente.value = crearConfiguracionCamposPaciente()
      registroConfiguracionPacientes.value = null
    }
    window.notificacion.notificacion(
      'warning',
      'No se pudo cargar la parametrización. Se usarán las validaciones habituales.'
    )
  }
}

const abrirConfiguracionCampos = () => {
  modalConfiguracionCampos.value?.open(
    camposConfiguracionPaciente.value,
    registroConfiguracionPacientes.value
  )
}

const onConfiguracionCamposGuardada = async (
  campos: IPacientesConfiguracionCampos
) => {
  const configuracionAnterior = JSON.parse(
    JSON.stringify(camposConfiguracionPaciente.value)
  ) as IPacientesConfiguracionCampos
  camposConfiguracionPaciente.value = JSON.parse(
    JSON.stringify(campos)
  ) as IPacientesConfiguracionCampos
  validator.reset()

  if (!form.value.copeId) {
    await actualizarValoresPorDefectoEnFormulario(configuracionAnterior)
  }

  await cargarConfiguracionCamposPaciente({
    conservarActual: true,
    actualizarCampos: false
  })
}

const aplicarValoresPorDefecto = async () => {
  for (const campo of camposPacienteConfigurables) {
    const valor =
      camposConfiguracionPaciente.value[campo.clave].valorPorDefecto.trim()
    const formulario = form.value as Record<string, unknown>
    const valorActual = formulario[campo.clave]
    if (
      !valor ||
      (valorActual !== null && valorActual !== undefined && valorActual !== '')
    )
      continue

    if (campo.clave === 'coppTipoListPoblaciones') {
      formulario[campo.clave] = valor
      poblacion_adicionales.value = valor
        .split(',')
        .map(item => item.trim())
        .filter(Boolean)
      continue
    }

    const valorAplicado = campo.tipoDato === 'seleccion' ? Number(valor) : valor
    if (typeof valorAplicado === 'number' && Number.isNaN(valorAplicado))
      continue
    formulario[campo.clave] = valorAplicado
  }

  searchOcupacion.value =
    tipos.value.tipoocupacion.find(
      item => Number(item.comaId) === Number(form.value.coppTipoOcupacion)
    )?.comaNombrelargo ?? null
  searchOrientacion.value =
    tipos.value.tipoorientacion.find(
      item => Number(item.comaId) === Number(form.value.coppTipoOrientacion)
    )?.comaNombrelargo ?? null
  searchPoblacion.value =
    tipos.value.tipopoblacion.find(
      item => Number(item.comaId) === Number(form.value.coppTipoPoblacion)
    )?.comaNombrelargo ?? null
  searchEtnia.value =
    tipos.value.tipoetnia.find(
      item => Number(item.comaId) === Number(form.value.coppTipoEtnia)
    )?.comaNombrelargo ?? null
  searchDiscapacidad.value =
    tipos.value.tipoincapacidad.find(
      item => Number(item.comaId) === Number(form.value.coppTipoIncapacidad)
    )?.comaNombrelargo ?? null

  try {
    if (form.value.copeTipoDivipola) {
      const {
        data: { body }
      } = await divipolaService.view(form.value.copeTipoDivipola)
      if (body) setDivipola(body)
    }
    if (form.value.coppTipoDivipola) {
      const {
        data: { body }
      } = await divipolaService.view(form.value.coppTipoDivipola)
      if (body)
        searchDivipolaNacimiento.value = `${body.nomPais}-${body.nomDepartamento}-${body.nomMunicipio}`
    }
  } catch {
    // Un valor de DIVIPOLA inválido no debe bloquear el registro del paciente.
  }
}

const actualizarValoresPorDefectoEnFormulario = async (
  configuracionAnterior: IPacientesConfiguracionCampos
) => {
  const formulario = form.value as Record<string, unknown>

  for (const campo of camposPacienteConfigurables) {
    const valorActual =
      campo.clave === 'coppTipoListPoblaciones'
        ? poblacion_adicionales.value.join(',')
        : formulario[campo.clave]
    const valorAnterior =
      configuracionAnterior[campo.clave].valorPorDefecto.trim()
    const estaVacio =
      valorActual === null ||
      valorActual === undefined ||
      String(valorActual).trim() === ''
    const conservaValorAnterior =
      valorAnterior !== '' && String(valorActual).trim() === valorAnterior

    if (!estaVacio && !conservaValorAnterior) continue

    formulario[campo.clave] = ''
    if (campo.clave === 'coppTipoListPoblaciones') {
      poblacion_adicionales.value = []
    }
  }

  await aplicarValoresPorDefecto()
}

const onIdentificacionCorregida = (payload: ICorreccionIdentificacion) => {
  form.value.copeIdentificacion = payload.nuevo.copeIdentificacion
  if (payload.nuevo.copeTipoIdentificacion !== null) {
    form.value.copeTipoIdentificacion = payload.nuevo.copeTipoIdentificacion
  }
  validatePatientIdentification()
}

const requiredForConfiguredField =
  (campo: CampoPacienteConfiguracionKey) => (value: unknown) => {
    if (!isFieldRequired(campo)) return true
    return validators.requerido(value)
  }

const validator = useValidator({
  form: {
    copeTipoIdentificacion: validators.requerido,
    copeIdentificacion: value => {
      if (
        value === null ||
        value === undefined ||
        String(value).trim() === ''
      ) {
        return false
      }
      if (value.length < 5) return 'Mínimo 5 caracteres'
      return true
    },
    copnNombre1: requiredForConfiguredField('copnNombre1'),
    copnNombre2: requiredForConfiguredField('copnNombre2'),
    copnApellido1: requiredForConfiguredField('copnApellido1'),
    copnApellido2: requiredForConfiguredField('copnApellido2'),
    coppFechanacimiento: requiredForConfiguredField('coppFechanacimiento'),
    coppTipoSexo: requiredForConfiguredField('coppTipoSexo'),
    coppTipoGenero: requiredForConfiguredField('coppTipoGenero'),
    copeTelefono: value => {
      if (isFieldHidden('copeTelefono')) return true
      if (
        value === null ||
        value === undefined ||
        String(value).trim() === ''
      ) {
        return isFieldRequired('copeTelefono')
          ? 'Este campo es requerido'
          : true
      }
      if (String(value).trim().length > 15) return 'Máximo 15 caracteres'
      if (String(value).trim().length < 10) return 'Mínimo 10 caracteres'
      return true
    },
    copeTelalternativo: requiredForConfiguredField('copeTelalternativo'),
    copeEmail: requiredForConfiguredField('copeEmail'),
    copeDireccion: requiredForConfiguredField('copeDireccion'),
    copeTipoDivipola: requiredForConfiguredField('copeTipoDivipola'),
    coppTipoDivipola: requiredForConfiguredField('coppTipoDivipola'),
    coppTipoZona: requiredForConfiguredField('coppTipoZona'),
    coppTipoBarrio: requiredForConfiguredField('coppTipoBarrio'),
    coppTipoNiveleducativo: requiredForConfiguredField(
      'coppTipoNiveleducativo'
    ),
    coppTipoOcupacion: requiredForConfiguredField('coppTipoOcupacion'),
    coppTipoOrientacion: requiredForConfiguredField('coppTipoOrientacion'),
    coppTipoEstadocivil: requiredForConfiguredField('coppTipoEstadocivil'),
    coppTipoPoblacion: requiredForConfiguredField('coppTipoPoblacion'),
    coppTipoListPoblaciones: requiredForConfiguredField(
      'coppTipoListPoblaciones'
    ),
    coppTipoEtnia: requiredForConfiguredField('coppTipoEtnia'),
    coppTipoIncapacidad: requiredForConfiguredField('coppTipoIncapacidad'),
    coppTipoEstado: requiredForConfiguredField('coppTipoEstado')
  },
  refs: { form }
})

const { validate, showError } = validator
const createRecord = async () => {
  try {
    form.value.isPaciente = true
    form.value.coppTipoListPoblaciones = poblacion_adicionales.value.join(',')
    errores.value = []
    window.notificacion.loading().circle()
    await validatePatientIdentification()
    const isFormCorrect = validate()

    if (!isFormCorrect) {
      window.notificacion.notificacion(
        'failure',
        'Por favor, corrija los errores en el formulario'
      )
      window.notificacion.loading().remove()
      return
    }

    // VALIDAR SI EL CHECK PACIENTE ESTA HABILITADO
    const {
      data: { body }
    } = await configuracionServicios.list({
      cocoCategoria: 'Pacientes',
      cocoCodigo: 'HAB_VALIDACION_DOCUMENTO'
    })

    if (!body || body.length !== 1) {
      window.notificacion.notificacion(
        'failure',
        'No se pudo validar el tipo de documento'
      )
      window.notificacion.loading().remove()
      return
    }

    const validateTypeDocument = body[0].cocoValue === 'NO' ? false : true

    // if (validateTypeDocument) return;
    if (validateTypeDocument && showValidTipid.value) return

    formateToUpperCaseFields()
    const payloadUser = generateUserByPaciente()
    if (!payloadUser) {
      window.notificacion.notificacion('failure', 'No se pudo crear el usuario')
      window.notificacion.loading().remove()
      return
    }

    if (!form.value.copeId) {
      form.value.userName = payloadUser.username
      form.value.user = payloadUser
    }
    const { data } = !form.value.copeId || altaCruzada.value
      ? await personaService.createPersona<IConfPersonasPaciente>(
          'pacientes',
          form.value
        )
      : await personaService.updatePersona<IConfPersonasPaciente>(
          `pacientes`,
          form.value.copeId,
          form.value
        )
    const result = data.body

    if (result) {
      if (result.notDataBase) {
        window.notificacion.notificacion(
          'info',
          'Se omitio el registro del paciente en la base de datos de la EPS,\n ya que la EPS no tiene bases de datos con la informacion suministrada.'
        )
      }
      emit('nuevoPaciente', { ...result })
      window.notificacion.notificacion('success', 'registrado')
    }
    window.notificacion.loading().remove()
    if (props.isModal) {
      cerrarModal()
    } else {
      router.push('/pacientes')
    }
  } catch (error) {
    errores.value = getErrores(error as AxiosError)
  }
  window.notificacion.loading().remove()
}

const formateToUpperCaseFields = () => {
  const {
    copnNombre1,
    copnNombre2,
    copnApellido1,
    copnApellido2,
    copeEmail,
    copeDireccion,
    coprPieFirma,
    coppTipoBarrio
  } = form.value
  form.value.copnNombre1 = copnNombre1 ? copnNombre1.toUpperCase() : copnNombre1
  form.value.copnNombre2 = copnNombre2 ? copnNombre2.toUpperCase() : copnNombre2
  form.value.copnApellido1 = copnApellido1
    ? copnApellido1.toUpperCase()
    : copnApellido1
  form.value.copnApellido2 = copnApellido2
    ? copnApellido2.toUpperCase()
    : copnApellido2
  form.value.copeEmail = copeEmail ? copeEmail.toUpperCase() : copeEmail
  form.value.copeDireccion = copeDireccion
    ? copeDireccion.toUpperCase()
    : copeDireccion
  form.value.coprPieFirma = coprPieFirma
    ? coprPieFirma.toUpperCase()
    : coprPieFirma
  form.value.coppTipoBarrio = coppTipoBarrio
    ? coppTipoBarrio.toUpperCase()
    : coppTipoBarrio
}

const cerrarModal = () => {
  const id = props.modalId ?? 'modal_paciente'
  closeModal(id)
  closeModal('modal-editar-paciente')
}

const reloadData = async () => {
  cargando.value = true
  form.value = {} as IConfPersonasPaciente
  await cargarConfiguracionCamposPaciente()

  await getTipoPoblaciones()

  const {
    data: { body: entidadBody }
  } = await entidadService.list()
  entidades.value = entidadBody

  const { data: contratoGenerales } = await contratoService.generales()
  tiposContratos.value = contratoGenerales.body

  const { data } =
    await personaService.getTiposPaciente<ITipoGeneralesPersona>()
  tipos.value = data.body
  const copeId = props.copeId ?? Number(route.params?.id)
  form.value.copeId = Number.isFinite(copeId) ? copeId : undefined
  if (form.value.copeId) {
    const { data } = await personaService.viewPersona<IConfPersonasPaciente>(
      `pacientes`,
      form.value.copeId
    )
    alreadyValidated.value = true
    const result = data.body
    form.value = result
    if (result.divipola)
      searchDivipola.value = `${
        result.divipola.nomPais +
        '-' +
        result.divipola.nomDepartamento +
        '-' +
        result.divipola.nomMunicipio
      }`
    if (result.divipolanacimiento)
      searchDivipolaNacimiento.value = `${
        result.divipolanacimiento.nomPais +
        '-' +
        result.divipolanacimiento.nomDepartamento +
        '-' +
        result.divipolanacimiento.nomMunicipio
      }`

    const poblacionesMultiplesTemp: string[] = []
    if (form.value.coppTipoListPoblaciones) {
      poblacionesMultiplesTemp.push(
        ...form.value.coppTipoListPoblaciones.split(',').map(String)
      )
    }

    if (form.value.coppTipoPoblacion) {
      tipoProblacionesMultipples.value =
        tipoProblacionesMultipples.value.filter(
          item => item.comaId !== form.value.coppTipoPoblacion
        )
    }
    if (form.value.coppTipoOcupacion) {
      searchOcupacion.value = tipos.value.tipoocupacion.find(
        item => item.comaId == form.value.coppTipoOcupacion
      )?.comaNombrelargo as string
    }
    if (form.value.coppTipoOrientacion) {
      searchOrientacion.value = tipos.value.tipoorientacion.find(
        item => item.comaId == form.value.coppTipoOrientacion
      )?.comaNombrelargo as string
    }
    if (form.value.coppTipoPoblacion) {
      searchPoblacion.value = tipos.value.tipopoblacion.find(
        item => item.comaId == form.value.coppTipoPoblacion
      )?.comaNombrelargo as string
    }
    if (form.value.coppTipoEtnia) {
      searchEtnia.value = tipos.value.tipoetnia.find(
        item => item.comaId == form.value.coppTipoEtnia
      )?.comaNombrelargo as string
    }
    if (form.value.coppTipoIncapacidad) {
      searchDiscapacidad.value = tipos.value.tipoincapacidad.find(
        item => item.comaId == form.value.coppTipoIncapacidad
      )?.comaNombrelargo as string
    }
    poblacion_adicionales.value = Array.from(new Set(poblacionesMultiplesTemp))

    if (form.value.coppTipoEtnia)
      etnia.value = tipos.value.tipoetnia.find(
        item => item.comaId == form.value.coppTipoEtnia
      )?.comaNombrelargo as string
  } else {
    await aplicarValoresPorDefecto()
  }
  cargando.value = false
}
const validatePatient = async () => {
  isSearching.value = true
  alreadyValidated.value = false
  const identificacion = normalizarIdentificacion(form.value.copeIdentificacion)
  if (!identificacion || !form.value.copeTipoIdentificacion) {
    window.notificacion.notificacion(
      'warning',
      'Por favor, ingrese el tipo y número de identificación'
    )
    isSearching.value = false
    return
  }
  form.value.copeIdentificacion = identificacion
  altaCruzada.value = false
  try {
    window.notificacion.loading().circle()
    const { data: dataPaciente } =
      await personaService.searchPersonaByIdent<IConfPersonasPaciente>(
        'pacientes',
        {
          search: identificacion,
          exactMatch: true,
          copeTipoIdentificacion: String(form.value.copeTipoIdentificacion)
        }
      )
    const pacientes = Array.isArray(dataPaciente.body) ? dataPaciente.body : []
    if (
      pacientes.length > 1 ||
      (pacientes.length === 1 && !coincideIdentificacion(pacientes[0], form.value.copeTipoIdentificacion, identificacion))
    ) {
      window.notificacion.notificacion('warning', 'La búsqueda devolvió una coincidencia ambigua para la identificación')
      return
    }
    const pacienteExistente = pacientes.length === 1
    if (pacienteExistente) {
      window.notificacion.notificacion(
        'info',
        'El paciente ya se encuentra registrado'
      )
      form.value = pacientes[0]
      if (form.value.divipola) setDivipola(form.value.divipola)
      alreadyValidated.value = true
      return
    }
    const { data } =
      await personaService.searchPersonaByIdent<IConfPersonasEmpleado>(
        'empleados',
        {
          search: identificacion,
          exactMatch: true,
          copeTipoIdentificacion: String(form.value.copeTipoIdentificacion)
        }
      )
    const empleados = Array.isArray(data.body) ? data.body : []
    if (
      empleados.length > 1 ||
      (empleados.length === 1 && !coincideIdentificacion(empleados[0], form.value.copeTipoIdentificacion, identificacion))
    ) {
      window.notificacion.notificacion('warning', 'La búsqueda devolvió una coincidencia ambigua para la identificación')
      return
    }
    if (!pacienteExistente && empleados.length === 1) {
      window.notificacion.notificacion(
        'info',
        'La persona ya se cuentra registrada como empleado'
      )
      altaCruzada.value = true
      form.value = mapEmpleadoToPaciente(empleados[0])
    }
    if (!pacienteExistente) {
      await aplicarValoresPorDefecto()
    }
    if (form.value.divipola) setDivipola(form.value.divipola)
    alreadyValidated.value = true
  } catch (error) {
    alreadyValidated.value = false
    errores.value = getErrores(error as AxiosError)
  } finally {
    isSearching.value = false
    window.notificacion.loading().remove()
  }
}
const setDivipola = (divipola: IConfDivipola) => {
  searchDivipola.value = `${
    divipola.nomPais +
    '-' +
    divipola.nomDepartamento +
    '-' +
    divipola.nomMunicipio
  }`
}
function mapEmpleadoToPaciente(datos: IConfPersonasEmpleado) {
  return {
    copeId: datos.copeId,
    copeIdentificacion: datos.copeIdentificacion,
    copeTipoIdentificacion: datos.copeTipoIdentificacion,
    copnNombre1: datos.copnNombre1,
    copnNombre2: datos.copnNombre2,
    copnApellido1: datos.copnApellido1,
    copnApellido2: datos.copnApellido2,
    coppFechanacimiento: datos.coppFechanacimiento,
    copeTelefono: datos.copeTelefono,
    copeEmail: datos.copeEmail,
    copeDireccion: datos.copeDireccion,
    divipola: datos.divipola,
    isPaciente: true,
    copeTipoDivipola: datos.copeTipoDivipola,
    coppTipoDivipola: datos.coppTipoDivipola
  } as IConfPersonasPaciente
}

const generateUserByPaciente = (): IAuthUser | null => {
  const { copeIdentificacion, copeEmail } = form.value
  const password = generarUuid()
  const { entidad: entidadSession } = _userSession //AUTO SELECCIONA LA IPS DEL USUARIO

  const entId = entidades.value.find(e => e.coenCodigo == entidadSession)

  const entidad = {
    userId: 9999,
    coenId: entId?.coenId,
    tabla: 'tbl_auth_user',
    entidad: entidadSession
  } as unknown as IConfRelacionesEntidades

  const role = {
    coenId: 1,
    role: 'PACIENTE',
    entidad: entId
  } as IAuthUserRole

  if (!copeIdentificacion) return null

  const payloadUser: IAuthUser = {
    username: `CC${copeIdentificacion}`,
    email: copeEmail ?? undefined,
    roles: [role],
    status: 0,
    passwordHash: password,
    passwordHashConfirmation: password,
    permisos: [] as IAuthAssignment[],
    sucursales: '[]',
    sucursalesFormat: [] as string[],
    entidades: [entidad] as IConfRelacionesEntidades[]
  }

  return payloadUser
}

const getTipoPoblaciones = async () => {
  const {
    data: { body }
  } = await maestraService.searchByType('TBL_TIPOPOBLACION')
  tipoProblacionesMultipples.value = body
}

const validatePatientIdentification = async () => {
  window.notificacion.loading().circle()
  if (form.value.copeTipoIdentificacion && form.value.coppFechanacimiento) {
    const { isValid, message } = await validateTypeIdentification(
      form.value.copeTipoIdentificacion!,
      form.value.coppFechanacimiento!
    )
    showValidTipid.value = !isValid
    if (!isValid) {
      window.notificacion.report(
        'info',
        'Edad incompatible con el documento',
        message
      )
    }
  }
  window.notificacion.loading().remove()
}

const onChangePoblacion = () => {
  tipoProblacionesMultipples.value = tipoProblacionesMultipples.value.filter(
    item => item.comaId !== form.value.coppTipoPoblacion
  )
}

onMounted(async () => {
  await reloadData()
  await validatePatientIdentification()
})
</script>
